import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/quotationApi";
import { useBranding, applyBrandingCSS } from "@/lib/branding";
import FloatingSaveBar from "@/components/FloatingSaveBar";
import BrandingPreview from "@/components/admin/branding/BrandingPreview";
import BusinessInfoSection from "@/components/admin/branding/BusinessInfoSection";
import PaymentInfoSection from "@/components/admin/branding/PaymentInfoSection";
import LogoSection from "@/components/admin/branding/LogoSection";
import ColorSection from "@/components/admin/branding/ColorSection";

const formFromBranding = (b) => ({
  firm_name: b.firm_name || "",
  tagline: b.tagline || "",
  primary_color: b.primary_color || "#C5A880",
  logo_storage_path: b.logo_storage_path || "",
  gst_number: b.gst_number || "",
  address: b.address || "",
  show_business_info: Boolean(b.show_business_info),
  bank_account_name: b.bank_account_name || "",
  bank_account_number: b.bank_account_number || "",
  bank_name: b.bank_name || "",
  ifsc_code: b.ifsc_code || "",
  upi_id: b.upi_id || "",
  qr_storage_path: b.qr_storage_path || "",
  show_payment_info: Boolean(b.show_payment_info),
});

export default function BrandingSettings() {
  const { branding, logoUrl, refresh } = useBranding();
  const [form, setForm] = useState(() => formFromBranding(branding));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingQr, setUploadingQr] = useState(false);
  const fileInputRef = useRef(null);
  const qrInputRef = useRef(null);

  const patch = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    setForm(formFromBranding(branding));
  }, [branding]);

  useEffect(() => {
    // Live preview for primary colour as user edits.
    applyBrandingCSS(form.primary_color);
  }, [form.primary_color]);

  const previewLogoUrl = form.logo_storage_path
    ? `${API}/files/${form.logo_storage_path}`
    : logoUrl;

  const makeUploadHandler = (field, setBusy, inputRef, successMsg) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      patch(field, r.data.storage_path);
      toast.success(successMsg);
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onUploadLogo = makeUploadHandler(
    "logo_storage_path", setUploading, fileInputRef, "Logo uploaded. Click Save to apply.",
  );
  const onUploadQr = makeUploadHandler(
    "qr_storage_path", setUploadingQr, qrInputRef, "QR uploaded. Click Save to apply.",
  );

  const save = async () => {
    const name = form.firm_name.trim();
    if (!name) {
      toast.error("Firm name cannot be empty");
      return;
    }
    if (!/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(form.primary_color)) {
      toast.error("Primary colour must be a hex value (e.g. #C5A880)");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/settings/branding`, {
        firm_name: name,
        tagline: form.tagline.trim(),
        logo_storage_path: form.logo_storage_path,
        primary_color: form.primary_color,
        gst_number: form.gst_number.trim(),
        address: form.address.trim(),
        show_business_info: form.show_business_info,
        bank_account_name: form.bank_account_name.trim(),
        bank_account_number: form.bank_account_number.trim(),
        bank_name: form.bank_name.trim(),
        ifsc_code: form.ifsc_code.trim().toUpperCase(),
        upi_id: form.upi_id.trim(),
        qr_storage_path: form.qr_storage_path,
        show_payment_info: form.show_payment_info,
      });
      await refresh();
      toast.success("Branding updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-luxe space-y-6" data-testid="branding-settings">
      <div>
        <div className="overline text-[#C5A880] mb-2">Branding</div>
        <p className="text-white/50 text-sm">
          Personalise how your firm appears across the app, public quotations and PDF exports.
        </p>
      </div>

      <BrandingPreview form={form} previewLogoUrl={previewLogoUrl} />

      {/* Firm name + tagline */}
      <div>
        <label className="label-luxe">Firm name</label>
        <input
          className="input-luxe"
          value={form.firm_name}
          onChange={(e) => patch("firm_name", e.target.value)}
          placeholder="e.g. LIVANA"
          maxLength={60}
          data-testid="branding-firm-name-input"
        />
        <p className="text-[11px] text-white/40 mt-1.5">
          Displayed at the top-left of every screen, on the public quotation page and inside the PDF header.
        </p>
      </div>

      <div>
        <label className="label-luxe">Tagline (optional)</label>
        <input
          className="input-luxe"
          value={form.tagline}
          onChange={(e) => patch("tagline", e.target.value)}
          placeholder="e.g. Interior Quotations"
          maxLength={80}
          data-testid="branding-tagline-input"
        />
      </div>

      <BusinessInfoSection form={form} patch={patch} />

      <PaymentInfoSection
        form={form}
        patch={patch}
        uploadingQr={uploadingQr}
        onUploadQr={onUploadQr}
        qrInputRef={qrInputRef}
      />

      <LogoSection
        form={form}
        patch={patch}
        uploading={uploading}
        onUploadLogo={onUploadLogo}
        fileInputRef={fileInputRef}
      />

      <ColorSection form={form} patch={patch} />

      <FloatingSaveBar
        onSave={save}
        saving={saving}
        label="Save branding"
        savingLabel="Saving…"
        testId="branding-save-btn"
      />
    </div>
  );
}
